// Commission Junction affiliate-event capture and lookup.
// Mirrors v1's include/global.php which sets the same two cookies on
// .ereleases.com when a visitor lands with ?cjevent=<token>:
//   _ERLS_CJEVENT_TOKEN  — 45 days (long-lived eReleases-owned token)
//   cje                  — 395 days, Secure (the canonical CJ cookie)
// Cookies are set on the .ereleases.com parent domain so they survive
// navigation between marketing and order subdomains. Local dev served
// from any other host will not see the cookies after they're "set" —
// that's expected and matches v1 behavior.

const CJ_COOKIE_DOMAIN = '.ereleases.com'
const DAY_IN_SECONDS = 86400

export function captureCjEvent() {
  const params = new URLSearchParams(window.location.search)
  const cjevent = params.get('cjevent')
  if (!cjevent) {
    return
  }
  setCookie('_ERLS_CJEVENT_TOKEN', cjevent, 45 * DAY_IN_SECONDS)
  setCookie('cje', cjevent, 395 * DAY_IN_SECONDS, { secure: true })
}

export function getCjEventToken() {
  return getCookie('cje') ?? getCookie('_ERLS_CJEVENT_TOKEN') ?? ''
}

export async function sha256Hex(input) {
  const bytes = new TextEncoder().encode(input)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

// Internal / test email domains whose orders must never be recorded as a
// Commission Junction conversion. v1 parity: complete.php excluded
// `daveseldon.com`; v2's ActiveCampaign push-suppression treats both
// `daveseldon.com` and `emoxie.com` as internal testers (see backend
// ExcludedEmails for the broader report-only list).
const INTERNAL_TEST_EMAIL_DOMAINS = ['daveseldon.com', 'emoxie.com']

export function isInternalTestEmail(email) {
  if (!email) {
    return false
  }
  const normalized = email.toLowerCase().trim()
  return INTERNAL_TEST_EMAIL_DOMAINS.some((domain) =>
    normalized.endsWith(`@${domain}`)
  )
}

// Per-order idempotency for the CJ conversion so a refresh, back-button or SPA
// remount of the welcome page cannot fire (and over-attribute) the same sale
// twice. Mirrors the localStorage guard the GTM tracker already uses.
const CJ_FIRED_KEY_PREFIX = 'cj:conversion:'

export function hasCjConversionFired(orderId) {
  try {
    return localStorage.getItem(`${CJ_FIRED_KEY_PREFIX}${orderId}`) !== null
  } catch {
    // localStorage unavailable (e.g. Safari private mode) — can't dedupe, so
    // allow the conversion rather than suppress a real sale.
    return false
  }
}

export function markCjConversionFired(orderId) {
  try {
    localStorage.setItem(`${CJ_FIRED_KEY_PREFIX}${orderId}`, '1')
  } catch {
    // localStorage unavailable — best-effort dedupe only.
  }
}

function setCookie(name, value, maxAgeSeconds, { secure = false } = {}) {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    'Path=/',
    `Domain=${CJ_COOKIE_DOMAIN}`,
  ]
  if (secure) {
    parts.push('Secure')
  }
  document.cookie = parts.join('; ')
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}
